import React, { useMemo } from "react";
import {
  Users,
  CheckCircle,
  XCircle,
  Search,
  FileCheck,
  RotateCcw,
  AlertCircle,
  FileText,
  ExternalLink,
  RefreshCw,
  Eye,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { GenericTable } from "@shared/components/GenericTable";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { cn } from "@shared/lib/utils";
import { TableAction, TableColumn } from "@shared/types/table";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { useViewBill } from "@features/bx/hooks";
import { useBillErrorsCsv } from "@features/cx/disconnect/hooks";

interface BillResultsDashboardProps {
  onApprove: () => void;
  onRegenerateAll: () => void;
  onRegenFailed: () => void;
  onDownloadLogs: () => void;
  data?: any[];
  totalCount?: number;
  isLoading?: boolean;
  isRegenerating?: boolean;
  isApproving?: boolean;
  taskId?: string;
  currentStatus?: string;
  generationStats?: {
    total: number;
    successful: number;
    failed: number;
    processed: number;
  };
  trackData?: any;
}

export const BillResultsDashboard = ({
  onApprove,
  onRegenerateAll,
  onRegenFailed,
  onDownloadLogs,
  data = [],
  totalCount = 0,
  isLoading = false,
  isRegenerating = false,
  isApproving = false,
  taskId,
  currentStatus,
  generationStats,
  trackData,
}: BillResultsDashboardProps) => {
  const currency = useCurrency();
  const { mutate: viewBill } = useViewBill();
  const { downloadErrorsCsv, isDownloading: isDownloadingLogs } =
    useBillErrorsCsv();
  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        header: "Invoice No",
        key: "invoiceNo",
        render: (value) => {
          return !value || value === "-" ? (
            <span className="text-neutral-300">—</span>
          ) : (
            <span className="text-blue-600 font-bold hover:underline cursor-pointer flex items-center gap-1">
              {value}
            </span>
          );
        },
      },
      { header: "Account No", key: "account" },
      { header: "Consumer Name", key: "consumer" },
      {
        header: "Bill Amount",
        key: "amount",
        render: (value) =>
          value ? (
            <span className="font-bold text-neutral-800">{value}</span>
          ) : (
            <span className="text-neutral-300">—</span>
          ),
      },
      {
        header: "Status",
        key: "status",
        render: (value) => (
          <Badge
            className={cn(
              "rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
              value?.toLowerCase() === "generated"
                ? "bg-green-50 text-green-600 border-green-100"
                : "bg-red-50 text-red-600 border-red-100",
            )}
            variant="outline"
          >
            {value || "FAILED"}
          </Badge>
        ),
      },
    ],
    [],
  );

  const actions: TableAction<any>[] = useMemo(
    () => [
      {
        label: "View",
        icon: <Eye className="w-4 h-4" />,
        disabled: (row) => {
          if (!row.id || row.status?.toLowerCase() !== "approved") {
            return true;
          }
          return false;
        },
        onClick: (row) => {
          if (row.id && row.status?.toLowerCase() === "approved") {
            viewBill(String(row.id));
          }
        },
      },
      // {
      //   label: "Retry",
      //   icon: <RefreshCw className="w-4 h-4" />,
      //   onClick: (row) => console.log("Retry", row),
      // },
    ],
    [viewBill],
  );

  const transformedData = useMemo(() => {
    return data.map((item: any) => ({
      id: item.id,
      invoiceNo: item.invoiceNo || "-",
      account: item.consumerId || "NA",
      consumer: item.consumerName || "NA",
      amount: currency + " " + item.billAmount,
      status: item.status || "Failed",
    }));
  }, [data]);

  console.log("transformedData", transformedData);

  const stats = useMemo(() => {
    if (generationStats && generationStats.total > 0) {
      return {
        total: generationStats.total,
        generated: generationStats.successful,
        failed: generationStats.failed,
      };
    }
    const total = transformedData.length || totalCount;
    const generated = transformedData.filter(
      (b) => b.status?.toLowerCase() === "generated",
    ).length;
    const failed = total - generated;
    return { total, generated, failed };
  }, [transformedData, totalCount, generationStats]);

  const disableRegenAndLogs =
    stats.total > 0 && stats.generated === stats.total;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <QuickStatsCard
          title="Total Consumers"
          count={trackData?.total || 0}
          icon={<Users className="w-6 h-6" />}
        />
        <QuickStatsCard
          title="Bills Generated"
          count={trackData?.generated || 0}
          icon={<CheckCircle className="w-6 h-6" />}
        />
        <QuickStatsCard
          title="Bills Failed"
          count={trackData?.failed || 0}
          icon={<XCircle className="w-6 h-6" />}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-end gap-4 bg-white p-4 rounded-xl border border-neutral-100 shadow-sm min-h-[72px]">
        <div className="flex items-center flex-wrap gap-2">
          <Button
            className="bg-green-600 hover:bg-green-700 h-10 px-4 rounded-lg font-bold gap-2"
            onClick={onApprove}
            disabled={
              isApproving || isRegenerating || currentStatus === "Closed"
            }
          >
            {isApproving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileCheck className="w-4 h-4" />
            )}
            {isApproving ? "Approving..." : "Approve & Send"}
          </Button>

          <Button
            variant="default"
            onClick={onRegenerateAll}
            disabled={
              isRegenerating || isApproving || currentStatus === "Closed"
            }
            className="gap-2"
          >
            {isRegenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {isRegenerating ? "Regenerating..." : "Regenerate All"}
          </Button>

          <Button
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-50 h-10 px-4 rounded-lg font-bold gap-2"
            onClick={onRegenFailed}
            disabled={disableRegenAndLogs || isRegenerating}
          >
            <RotateCcw className="w-4 h-4" />
            Regen Failed
          </Button>

          <Button
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-50 h-10 px-4 rounded-lg font-bold gap-2"
            onClick={() => {
              if (taskId) {
                downloadErrorsCsv(taskId);
              } else {
                onDownloadLogs?.();
              }
            }}
            disabled={isDownloadingLogs || disableRegenAndLogs}
          >
            {isDownloadingLogs ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {isDownloadingLogs ? "Downloading..." : "Failed Logs"}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-neutral-100 shadow-sm overflow-hidden">
        <GenericTable
          columns={columns}
          data={transformedData}
          rowKey="id"
          actions={actions}
          actionsType="icons"
          loading={isLoading}
          onSearch={(val) => console.log("Searching for:", val)}
          searchPlaceholder="Search bills by ID, account or consumer..."
        />
      </div>
    </div>
  );
};
